import {
  all,
  call,
  getContext,
  put,
  select,
  takeLatest
} from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import {
  GET_ALL_PROJECTS,
  GET_PROJECT_DETAILS,
  VALIDATE_PROJECT_ID
} from 'actions/projects/constants';

import { UPDATE_PROJECT_SESSION_DATA } from 'actions/projectData/constants';

import {
  getAllProjectsSuccess,
  getAllProjectsError,
  getProjectDetailsSuccess,
  getProjectDetailsError
} from 'actions/projects';

import {
  updateProjectSessionDataSuccess,
  updateProjectSessionDataError
} from 'actions/projectData';

import normaliseError from 'utils/normaliseError';
import { handleUnauthorised } from 'utils/authHelpers';

import { API_URL, HTTP_VERBS } from 'utils/constants';
import {
  rawActiveProjectIdSelector,
  validatedActiveProjectIdSelector,
  activeProjectSelector
} from 'reducers/projects/selectors';

/* Project details */
export function* getProjectDetailsSaga({ projectId }) {
  const requestUrl = `${API_URL}/projects/${projectId}`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getProjectDetailsSuccess(projectId, response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getProjectDetailsError(projectId, transformedError));
  }
}

export function* getProjectDetailsWatcher() {
  yield takeLatest(GET_PROJECT_DETAILS, getProjectDetailsSaga);
}

/* Get all Projects */
export function* getAllProjectsSaga() {
  const requestUrl = `${API_URL}/user/projects`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getAllProjectsSuccess(response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getAllProjectsError(transformedError));
  }
}

export function* updateProjectSessionDataSaga({ projectId, values, actions }) {
  const { project_settings } = yield select(activeProjectSelector);
  const requestUrl = `${API_URL}/projects/${projectId}`;
  const opts = {
    method: HTTP_VERBS.PUT,
    url: requestUrl,
    data: {
      project_settings: {
        in_channel_chat_enabled: values.chatEnabled,
        annotations_enabled: values.annotationsEnabled,
        annotations_settings: {
          cursor_tracking: values.cursorTracking,
          disappearing_ink: values.disappearingInk,
          draw_tool: values.drawTool
        },
        password_expiry: project_settings.password_expiry,
        attendee_wide_mode_default: values.attendeeWideMode
      }
    }
  };

  try {
    yield call(axios, opts);
    yield* getProjectDetailsSaga({ projectId });
    yield call(toastr.success, 'Project updated successfully');
    yield put(updateProjectSessionDataSuccess());
  } catch (error) {
    const transformedError = normaliseError(error);

    actions.resetForm();
    yield call(toastr.error, 'Project update failed');
    yield put(updateProjectSessionDataError(transformedError));
  }
}

export function* getAllProjectsWatcher() {
  yield takeLatest(GET_ALL_PROJECTS, getAllProjectsSaga);
}

export function* updateProjectSessionDataWatcher() {
  yield takeLatest(UPDATE_PROJECT_SESSION_DATA, updateProjectSessionDataSaga);
}

export function* validateProjectIdSaga() {
  const rawProjectId = yield select(rawActiveProjectIdSelector);
  const validatedProjectId = yield select(validatedActiveProjectIdSelector);

  // If raw project ID exists but validation failed (validated is null),
  // the user doesn't have access to this project
  if (rawProjectId && !validatedProjectId) {
    const history = yield getContext('history');
    yield call(handleUnauthorised, history);
  }
}

export function* validateProjectIdWatcher() {
  yield takeLatest(VALIDATE_PROJECT_ID, validateProjectIdSaga);
}

export default function* rootSaga() {
  yield all([
    call(getProjectDetailsWatcher),
    call(getAllProjectsWatcher),
    call(updateProjectSessionDataWatcher),
    call(validateProjectIdWatcher)
  ]);
}
